import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FilterDropdown from '../../components/common/FilterDropdown';
import {
    PlusIcon,
    ScaleIcon,
    ShieldExclamationIcon,
    PencilSquareIcon,
    TrashIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const MAX_CATEGORY_POINTS = 3;

// Which fields each trigger type needs, and how the threshold is worded.
const RULE_TYPE_CONFIG = {
    repeat_category: {
        label: 'Repeat Category Infraction',
        categoryType: 'demerit',
        categoryRequired: true,
        showSeverity: false,
        thresholdLabel: 'Repeat Threshold',
        thresholdHelper: 'Times the same infraction is logged within the period',
        unit: 'times',
        tiers: ['detention', 'suspension', 'expelled']
    },
    demerit_threshold: {
        label: 'Cumulative Demerits Threshold',
        categoryType: 'demerit',
        categoryRequired: false,
        showSeverity: false,
        thresholdLabel: 'Max Demerit Points Allowed',
        thresholdHelper: 'Triggers once total demerit points within the period reach this number',
        unit: 'pts',
        tiers: ['detention', 'suspension', 'expelled']
    },
    severity_trigger: {
        label: 'Immediate Severity Incident',
        categoryType: 'demerit',
        categoryRequired: false,
        showSeverity: true,
        thresholdLabel: 'Incidents Required',
        thresholdHelper: 'Incidents of the chosen severity within the period (use 1 for immediate)',
        unit: 'times',
        tiers: ['detention', 'suspension', 'expelled']
    },
    merit_milestone: {
        label: 'Positive Merit Milestone',
        categoryType: 'merit',
        categoryRequired: false,
        showSeverity: false,
        thresholdLabel: 'Merit Points to Reach',
        thresholdHelper: 'Triggers once total merit points within the period reach this number',
        unit: 'pts',
        tiers: ['exemplary', 'good']
    }
};

const TIER_LABELS = {
    exemplary: 'Exemplary',
    good: 'Good Standing',
    detention: 'Detention',
    suspension: 'Suspension',
    expelled: 'Expelled'
};

const SEVERITY_OPTIONS = [
    { name: 'Minor', value: 'minor' },
    { name: 'Moderate', value: 'moderate' },
    { name: 'Major', value: 'major' },
    { name: 'Severe', value: 'severe' }
];

const ACCRUAL_OPTIONS = [
    { name: 'Weekly (Trailing 7 Days)', value: 'weekly' },
    { name: 'Monthly (Trailing 30 Days)', value: 'monthly' },
    { name: 'Current Academic Term', value: 'term' }
];

const RULE_TYPE_OPTIONS = Object.entries(RULE_TYPE_CONFIG).map(([value, c]) => ({ name: c.label, value }));

const DisciplineCategoryManager = () => {
    const { get, post, put, del } = useApi();
    const { showToast } = useAppState();
    const queryClient = useQueryClient();

    // Active Admin Tab: 'categories' | 'rules'
    const [activeAdminTab, setActiveAdminTab] = useState('categories');

    // Category States
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [filterCategoryType, setFilterCategoryType] = useState('all'); // 'all' | 'merit' | 'demerit'
    const [searchCategory, setSearchCategory] = useState('');

    const [categoryFormData, setCategoryFormData] = useState({
        name: '',
        type: 'demerit',
        points: MAX_CATEGORY_POINTS,
        is_active: true
    });

    // Rule States
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [filterRuleTier, setFilterRuleTier] = useState('all');
    const [searchRule, setSearchRule] = useState('');

    const [ruleFormData, setRuleFormData] = useState({
        name: '',
        rule_type: 'repeat_category',
        category: '',
        severity: '',
        cron_accrual_period: 'weekly',
        repeat_threshold: 3,
        standing_tier: 'detention',
        is_active: true
    });

    // 1. Fetch Categories
    const { data: categories = [], isLoading: loadingCategories } = useQuery({
        queryKey: ['discipline-categories'],
        queryFn: () => get('/api/discipline/categories/'),
        staleTime: 60 * 1000,
    });

    // 2. Fetch Rules
    const { data: rules = [], isLoading: loadingRules } = useQuery({
        queryKey: ['discipline-rules'],
        queryFn: async () => {
            try {
                const res = await get('/api/discipline/rules/');
                return Array.isArray(res) ? res : [];
            } catch (e) {
                return [];
            }
        },
        staleTime: 60 * 1000,
    });

    // Category Mutations
    const createCategoryMutation = useMutation({
        mutationFn: (data) => post('/api/discipline/categories/', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-categories'] });
            showToast?.('Category created successfully', 'success');
            handleCloseCategoryModal();
        },
        onError: (err) => showToast?.(err.message || 'Failed to create category', 'error')
    });

    const updateCategoryMutation = useMutation({
        mutationFn: ({ id, data }) => put(`/api/discipline/categories/${id}/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-categories'] });
            showToast?.('Category updated successfully', 'success');
            handleCloseCategoryModal();
        },
        onError: (err) => showToast?.(err.message || 'Failed to update category', 'error')
    });

    const toggleCategoryStatusMutation = useMutation({
        mutationFn: ({ id, active }) => post(`/api/discipline/categories/${id}/${active ? 'deactivate' : 'activate'}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-categories'] });
            showToast?.('Category status updated', 'success');
        },
        onError: (err) => showToast?.(err.message || 'Failed to update status', 'error')
    });

    // Rule Mutations
    const createRuleMutation = useMutation({
        mutationFn: (data) => post('/api/discipline/rules/', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-rules'] });
            showToast?.('Governance rule created successfully', 'success');
            handleCloseRuleModal();
        },
        onError: (err) => showToast?.(err.message || 'Failed to create rule', 'error')
    });

    const updateRuleMutation = useMutation({
        mutationFn: ({ id, data }) => put(`/api/discipline/rules/${id}/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-rules'] });
            showToast?.('Governance rule updated successfully', 'success');
            handleCloseRuleModal();
        },
        onError: (err) => showToast?.(err.message || 'Failed to update rule', 'error')
    });

    const toggleRuleStatusMutation = useMutation({
        mutationFn: ({ id }) => post(`/api/discipline/rules/${id}/toggle-active/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-rules'] });
            showToast?.('Rule status toggled', 'success');
        },
        onError: (err) => showToast?.(err.message || 'Failed to toggle rule', 'error')
    });

    const deleteRuleMutation = useMutation({
        mutationFn: ({ id }) => del(`/api/discipline/rules/${id}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discipline-rules'] });
            showToast?.('Rule deleted', 'success');
        },
        onError: (err) => showToast?.(err.message || 'Failed to delete rule', 'error')
    });

    // Category Handlers
    const handleOpenCreateCategory = () => {
        setEditingCategory(null);
        setCategoryFormData({
            name: '',
            type: 'demerit',
            points: MAX_CATEGORY_POINTS,
            is_active: true
        });
        setIsCategoryModalOpen(true);
    };

    const handleOpenEditCategory = (cat) => {
        setEditingCategory(cat);
        setCategoryFormData({
            name: cat.name,
            type: cat.type,
            points: cat.points,
            is_active: cat.is_active
        });
        setIsCategoryModalOpen(true);
    };

    const handleCloseCategoryModal = () => {
        setIsCategoryModalOpen(false);
        setEditingCategory(null);
    };

    const handleCategorySubmit = (e) => {
        e.preventDefault();
        if (!categoryFormData.name.trim()) {
            showToast?.('Category name is required', 'error');
            return;
        }
        if (editingCategory) {
            updateCategoryMutation.mutate({ id: editingCategory.id, data: categoryFormData });
        } else {
            createCategoryMutation.mutate(categoryFormData);
        }
    };

    // Rule Handlers
    const handleOpenCreateRule = () => {
        setEditingRule(null);
        setRuleFormData({
            name: '',
            rule_type: 'repeat_category',
            category: '',
            severity: '',
            cron_accrual_period: 'weekly',
            repeat_threshold: 3,
            standing_tier: 'detention',
            is_active: true
        });
        setIsRuleModalOpen(true);
    };

    const handleOpenEditRule = (rule) => {
        setEditingRule(rule);
        setRuleFormData({
            name: rule.name,
            rule_type: rule.rule_type,
            category: rule.category || '',
            severity: rule.severity || '',
            cron_accrual_period: rule.cron_accrual_period,
            repeat_threshold: rule.repeat_threshold,
            standing_tier: rule.standing_tier,
            is_active: rule.is_active
        });
        setIsRuleModalOpen(true);
    };

    const handleCloseRuleModal = () => {
        setIsRuleModalOpen(false);
        setEditingRule(null);
    };

    const handleRuleSubmit = (e) => {
        e.preventDefault();
        if (!ruleFormData.name.trim()) {
            showToast?.('Rule name is required', 'error');
            return;
        }
        const cfg = RULE_TYPE_CONFIG[ruleFormData.rule_type];
        if (cfg.categoryRequired && !ruleFormData.category) {
            showToast?.('Select the category this rule applies to', 'error');
            return;
        }
        if (cfg.showSeverity && !ruleFormData.severity) {
            showToast?.('Select the severity this rule applies to', 'error');
            return;
        }
        const payload = {
            ...ruleFormData,
            category: ruleFormData.category ? parseInt(ruleFormData.category) : null,
            severity: cfg.showSeverity ? ruleFormData.severity : null,
            repeat_threshold: parseInt(ruleFormData.repeat_threshold) || 1
        };
        if (editingRule) {
            updateRuleMutation.mutate({ id: editingRule.id, data: payload });
        } else {
            createRuleMutation.mutate(payload);
        }
    };

    // Switching trigger type drops fields that no longer apply and picks a valid tier
    const handleRuleTypeChange = (ruleType) => {
        const cfg = RULE_TYPE_CONFIG[ruleType];
        setRuleFormData(prev => ({
            ...prev,
            rule_type: ruleType,
            category: '',
            severity: '',
            standing_tier: cfg.tiers.includes(prev.standing_tier) ? prev.standing_tier : cfg.tiers[0]
        }));
    };

    // Filters
    const filteredCategories = categories.filter(cat => {
        if (filterCategoryType !== 'all' && cat.type !== filterCategoryType) return false;
        if (searchCategory) {
            const q = searchCategory.toLowerCase();
            return cat.name.toLowerCase().includes(q);
        }
        return true;
    });

    const filteredRules = rules.filter(r => {
        if (filterRuleTier !== 'all' && r.standing_tier !== filterRuleTier) return false;
        if (searchRule) {
            const q = searchRule.toLowerCase();
            const matchesName = r.name?.toLowerCase().includes(q);
            const matchesCat = r.category_name?.toLowerCase().includes(q);
            const matchesTier = r.standing_tier_display?.toLowerCase().includes(q) || r.standing_tier?.toLowerCase().includes(q);
            if (!matchesName && !matchesCat && !matchesTier) return false;
        }
        return true;
    });

    const meritCount = categories.filter(c => c.type === 'merit').length;
    const demeritCount = categories.filter(c => c.type === 'demerit').length;

    const getTierBadge = (tier) => {
        switch (tier) {
            case 'exemplary':
                return <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">Exemplary</span>;
            case 'good':
                return <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200">Good Standing</span>;
            case 'detention':
                return <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">Detention</span>;
            case 'suspension':
                return <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">Suspension</span>;
            case 'expelled':
                return <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-red-100 text-red-900 border border-red-300">Expelled</span>;
            default:
                return <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700">{tier}</span>;
        }
    };

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Redesigned Header Matching Learning Areas Page Style (Screenshot 3) */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-[#18216D] tracking-tight">Character Items</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">
                        Manage character categories, point weightings, and automated governance rules.
                    </p>
                </div>
                <div className="flex flex-col gap-3 w-full xl:w-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-full sm:w-56">
                        {activeAdminTab === 'categories' ? (
                            <FilterDropdown
                                value={filterCategoryType}
                                onChange={(val) => setFilterCategoryType(val || 'all')}
                                options={[
                                    { name: `All Categories (${categories.length})`, value: 'all' },
                                    { name: `Merits (${meritCount})`, value: 'merit' },
                                    { name: `Demerits (${demeritCount})`, value: 'demerit' },
                                ]}
                                placeholder="Filter by Type"
                                className="w-full"
                            />
                        ) : (
                            <FilterDropdown
                                value={filterRuleTier}
                                onChange={(val) => setFilterRuleTier(val || 'all')}
                                options={[
                                    { name: `All Tiers (${rules.length})`, value: 'all' },
                                    { name: 'Exemplary', value: 'exemplary' },
                                    { name: 'Good Standing', value: 'good' },
                                    { name: 'Detention', value: 'detention' },
                                    { name: 'Suspension', value: 'suspension' },
                                    { name: 'Expelled', value: 'expelled' },
                                ]}
                                placeholder="Filter by Tier"
                                className="w-full"
                            />
                        )}
                    </div>

                    <button
                        onClick={activeAdminTab === 'categories' ? handleOpenCreateCategory : handleOpenCreateRule}
                        className="px-6 py-3 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] shadow-lg shadow-indigo-900/10 transition-all font-black text-xs uppercase tracking-widest flex items-center justify-center space-x-2 active:scale-95 whitespace-nowrap"
                    >
                        <PlusIcon className="w-4 h-4 stroke-[3]" />
                        <span>{activeAdminTab === 'categories' ? 'Add Category' : 'Create Rule'}</span>
                    </button>
                    </div>

                    <div className="relative w-full">
                        <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder={activeAdminTab === 'categories' ? 'Search category name...' : 'Search standing rules...'}
                            value={activeAdminTab === 'categories' ? searchCategory : searchRule}
                            onChange={(e) => (activeAdminTab === 'categories' ? setSearchCategory(e.target.value) : setSearchRule(e.target.value))}
                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#18216D] focus:ring-2 focus:ring-[#18216D]/10 transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* Underline Tabs Matching Lesson Records */}
            <div className="border-b border-gray-200">
                <nav className="flex justify-center space-x-8" aria-label="Character Items Tabs">
                    <button
                        type="button"
                        onClick={() => setActiveAdminTab('categories')}
                        className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                            activeAdminTab === 'categories'
                                ? 'text-[#18216D] border-[#18216D]'
                                : 'text-slate-400 hover:text-slate-600 border-transparent'
                        }`}
                    >
                        <ScaleIcon className={`w-4 h-4 ${activeAdminTab === 'categories' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                        <span>Categories ({categories.length})</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveAdminTab('rules')}
                        className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                            activeAdminTab === 'rules'
                                ? 'text-[#18216D] border-[#18216D]'
                                : 'text-slate-400 hover:text-slate-600 border-transparent'
                        }`}
                    >
                        <ShieldExclamationIcon className={`w-4 h-4 ${activeAdminTab === 'rules' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                        <span>Standing Rules ({rules.length})</span>
                    </button>
                </nav>
            </div>

            {/* TAB 1: CATEGORIES */}
            {activeAdminTab === 'categories' && (
                <div className="space-y-6 animate-fadeIn">
                    {/* Categories Table */}
                    {loadingCategories ? (
                        <div className="flex justify-center py-16">
                            <LoadingSpinner />
                        </div>
                    ) : filteredCategories.length === 0 ? (
                        <div className="bg-white rounded-3xl p-16 text-center border border-slate-100 shadow-sm">
                            <ScaleIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-black text-[#18216D] uppercase">No Categories Found</h3>
                            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                Add merit or demerit categories with custom point values for teachers to log.
                            </p>
                            <button
                                onClick={handleOpenCreateCategory}
                                className="mt-6 px-6 py-2.5 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-wider"
                            >
                                Create First Category
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50/80 border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                        <tr>
                                            <th className="px-6 py-4">Category Name</th>
                                            <th className="px-6 py-4">Type</th>
                                            <th className="px-6 py-4 text-center">Point Weight</th>
                                            <th className="px-6 py-4 text-center">Teacher Logging</th>
                                            <th className="px-6 py-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
                                        {filteredCategories.map((cat) => (
                                            <tr key={cat.id} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-black text-[#18216D]">{cat.name}</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                            cat.type === 'merit'
                                                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                : 'bg-rose-50 text-rose-700 border border-rose-200'
                                                        }`}
                                                    >
                                                        {cat.type === 'merit' ? 'Positive Merit' : 'Demerit'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-block px-3 py-1 rounded-xl text-xs font-black ${
                                                        cat.type === 'merit' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                                                    }`}>
                                                        {cat.type === 'merit' ? `+${cat.points}` : `-${cat.points}`} pts
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span
                                                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                            cat.is_active
                                                                ? 'bg-blue-50 text-blue-700'
                                                                : 'bg-slate-100 text-slate-400'
                                                        }`}
                                                    >
                                                        {cat.is_active ? 'Active' : 'Disabled'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleOpenEditCategory(cat)}
                                                            className="p-2 text-slate-400 hover:text-[#18216D] hover:bg-slate-100 rounded-xl transition-colors"
                                                            title="Edit Category"
                                                        >
                                                            <PencilSquareIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => toggleCategoryStatusMutation.mutate({ id: cat.id, active: cat.is_active })}
                                                            className={`p-2 rounded-xl transition-colors ${
                                                                cat.is_active
                                                                    ? 'text-rose-400 hover:text-rose-600 hover:bg-rose-50'
                                                                    : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'
                                                            }`}
                                                            title={cat.is_active ? 'Deactivate Category' : 'Activate Category'}
                                                        >
                                                            {cat.is_active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: STANDING RULES */}
            {activeAdminTab === 'rules' && (
                <div className="space-y-6 animate-fadeIn">
                    {/* Rules Table */}
                    {loadingRules ? (
                        <div className="flex justify-center py-16">
                            <LoadingSpinner />
                        </div>
                    ) : filteredRules.length === 0 ? (
                        <div className="bg-white rounded-3xl p-16 text-center border border-slate-100 shadow-sm">
                            <ShieldExclamationIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-black text-[#18216D] uppercase">No Dynamic Rules Defined</h3>
                            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                Create automated rules to define when scholars breach repeat thresholds, trigger pastoral check-ins, or require detentions.
                            </p>
                            <button
                                onClick={handleOpenCreateRule}
                                className="mt-6 px-6 py-2.5 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-wider"
                            >
                                Create First Rule
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50/80 border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                        <tr>
                                            <th className="px-6 py-4">Rule Name</th>
                                            <th className="px-6 py-4">Trigger Type</th>
                                            <th className="px-6 py-4">Target Scope</th>
                                            <th className="px-6 py-4">Accrual Window</th>
                                            <th className="px-6 py-4 text-center">Threshold</th>
                                            <th className="px-6 py-4 text-center">Standing Tier</th>
                                            <th className="px-6 py-4 text-center">Status</th>
                                            <th className="px-6 py-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
                                        {filteredRules.map((rule) => (
                                            <tr key={rule.id} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-black text-[#18216D]">{rule.name}</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-[11px] font-bold text-slate-600">
                                                        {rule.rule_type_display || rule.rule_type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-xs font-bold text-slate-800">
                                                        {rule.category_name || 'All Infractions'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1.5 text-slate-500 font-bold">
                                                        <ClockIcon className="w-3.5 h-3.5 text-slate-400" />
                                                        <span className="capitalize">{rule.cron_accrual_period}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="px-2.5 py-1 rounded-xl bg-slate-100 text-slate-800 font-black">
                                                        ≥ {rule.repeat_threshold} {RULE_TYPE_CONFIG[rule.rule_type]?.unit || 'times'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {getTierBadge(rule.standing_tier)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span
                                                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                            rule.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                                                        }`}
                                                    >
                                                        {rule.is_active ? 'Active' : 'Disabled'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleOpenEditRule(rule)}
                                                            className="p-2 text-slate-400 hover:text-[#18216D] hover:bg-slate-100 rounded-xl transition-colors"
                                                            title="Edit Rule"
                                                        >
                                                            <PencilSquareIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => toggleRuleStatusMutation.mutate({ id: rule.id })}
                                                            className={`p-2 rounded-xl transition-colors ${
                                                                rule.is_active
                                                                    ? 'text-rose-400 hover:text-rose-600 hover:bg-rose-50'
                                                                    : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'
                                                            }`}
                                                            title={rule.is_active ? 'Disable Rule' : 'Enable Rule'}
                                                        >
                                                            {rule.is_active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (window.confirm(`Delete rule '${rule.name}'?`)) {
                                                                    deleteRuleMutation.mutate({ id: rule.id });
                                                                }
                                                            }}
                                                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                                                            title="Delete Rule"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* CATEGORY MODAL: Cleaned up — NO Cron Period or Repeat Thresholds */}
            {isCategoryModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn overflow-hidden">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[88vh]">
                        <div className="bg-[#18216D] px-8 py-6 text-white flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight">
                                    {editingCategory ? 'Edit Category' : 'Create Category'}
                                </h3>
                                <p className="text-indigo-200 text-xs mt-0.5">
                                    Define category classification, points weighting, and active status.
                                </p>
                            </div>
                            <button
                                onClick={handleCloseCategoryModal}
                                className="text-white/70 hover:text-white p-2 rounded-xl transition-colors"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleCategorySubmit} className="p-8 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
                            <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                    Category Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={categoryFormData.name}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                                    placeholder="e.g. Academic Excellence, Disrespectful Conduct"
                                    className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] focus:ring-2 focus:ring-[#18216D]/10 outline-none transition-all"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                        Type *
                                    </label>
                                    <FilterDropdown
                                        className="w-full"
                                        value={categoryFormData.type}
                                        onChange={(val) => setCategoryFormData({ ...categoryFormData, type: val || 'demerit' })}
                                        options={[
                                            { name: 'Demerit (Infraction)', value: 'demerit' },
                                            { name: 'Merit (Commendation)', value: 'merit' }
                                        ]}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-2">
                                        Points Value *
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max={MAX_CATEGORY_POINTS}
                                        required
                                        value={categoryFormData.points}
                                        onChange={(e) => {
                                            const n = parseInt(e.target.value) || 1;
                                            setCategoryFormData({ ...categoryFormData, points: Math.min(MAX_CATEGORY_POINTS, Math.max(1, n)) });
                                        }}
                                        className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] outline-none"
                                    />
                                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                                        {categoryFormData.type === 'merit' ? '+' : '-'}1 to {MAX_CATEGORY_POINTS} points (max {MAX_CATEGORY_POINTS})
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <input
                                    type="checkbox"
                                    id="category_is_active_toggle"
                                    checked={categoryFormData.is_active}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, is_active: e.target.checked })}
                                    className="w-4 h-4 text-[#18216D] rounded border-slate-300 focus:ring-[#18216D]"
                                />
                                <label htmlFor="category_is_active_toggle" className="text-xs font-bold text-slate-700 cursor-pointer">
                                    Active for teacher logging
                                </label>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={handleCloseCategoryModal}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createCategoryMutation.isPending || updateCategoryMutation.isPending}
                                    className="px-6 py-2.5 bg-[#18216D] hover:bg-[#121952] text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                                >
                                    {editingCategory ? 'Save Changes' : 'Create Category'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DYNAMIC RULE MODAL: Manages Accrual, Repeat Thresholds, Standing Tiers */}
            {isRuleModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn overflow-hidden">
                    <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[88vh]">
                        <div className="bg-[#18216D] px-8 py-6 text-white flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight">
                                    {editingRule ? 'Edit Governance Rule' : 'Create Governance Rule'}
                                </h3>
                                <p className="text-indigo-200 text-xs mt-0.5">
                                    Define automated repeat thresholds, accrual periods, and standing triggers.
                                </p>
                            </div>
                            <button
                                onClick={handleCloseRuleModal}
                                className="text-white/70 hover:text-white p-2 rounded-xl transition-colors"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleRuleSubmit} className="p-8 space-y-5 flex-1 overflow-y-auto custom-scrollbar">
                            <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-[#18216D] mb-1.5">
                                    Rule Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={ruleFormData.name}
                                    onChange={(e) => setRuleFormData({ ...ruleFormData, name: e.target.value })}
                                    placeholder="e.g. 3x Homework Breaches -> Pastoral Check-in"
                                    className="w-full px-4 py-2.5 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] outline-none"
                                />
                            </div>

                            {(() => {
                                const cfg = RULE_TYPE_CONFIG[ruleFormData.rule_type];
                                const labelCls = 'block text-xs font-black uppercase tracking-wider text-[#18216D] mb-1.5';
                                const categoryOptions = [
                                    ...(cfg.categoryRequired ? [] : [{
                                        name: `All ${cfg.categoryType === 'merit' ? 'Merit' : 'Demerit'} Categories`,
                                        value: ''
                                    }]),
                                    ...categories
                                        .filter(c => c.type === cfg.categoryType)
                                        .map(c => ({ name: c.name, value: String(c.id) }))
                                ];
                                const tierValues = cfg.tiers.includes(ruleFormData.standing_tier)
                                    ? cfg.tiers
                                    : [...cfg.tiers, ruleFormData.standing_tier];
                                return (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelCls}>Rule Trigger Type *</label>
                                                <FilterDropdown
                                                    className="w-full"
                                                    value={ruleFormData.rule_type}
                                                    onChange={(val) => val && handleRuleTypeChange(val)}
                                                    options={RULE_TYPE_OPTIONS}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelCls}>
                                                    {cfg.categoryRequired ? 'Infraction Category *' : 'Target Category Scope'}
                                                </label>
                                                <FilterDropdown
                                                    className="w-full"
                                                    value={ruleFormData.category ? String(ruleFormData.category) : ''}
                                                    onChange={(val) => setRuleFormData({ ...ruleFormData, category: val || '' })}
                                                    options={categoryOptions}
                                                    placeholder={cfg.categoryRequired ? 'Select category' : undefined}
                                                />
                                            </div>
                                        </div>

                                        {cfg.showSeverity && (
                                            <div>
                                                <label className={labelCls}>Severity *</label>
                                                <FilterDropdown
                                                    className="w-full"
                                                    value={ruleFormData.severity}
                                                    onChange={(val) => setRuleFormData({ ...ruleFormData, severity: val || '' })}
                                                    options={SEVERITY_OPTIONS}
                                                    placeholder="Select severity"
                                                />
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelCls}>Cron Accrual Period *</label>
                                                <FilterDropdown
                                                    className="w-full"
                                                    value={ruleFormData.cron_accrual_period}
                                                    onChange={(val) => val && setRuleFormData({ ...ruleFormData, cron_accrual_period: val })}
                                                    options={ACCRUAL_OPTIONS}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelCls}>{cfg.thresholdLabel} *</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    required
                                                    value={ruleFormData.repeat_threshold}
                                                    onChange={(e) => setRuleFormData({ ...ruleFormData, repeat_threshold: parseInt(e.target.value) || 1 })}
                                                    className="w-full px-4 py-2.5 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#18216D] outline-none"
                                                />
                                                <span className="text-[10px] text-slate-400 mt-0.5 block">{cfg.thresholdHelper}</span>
                                            </div>
                                        </div>

                                        <div>
                                            <label className={labelCls}>Resulting Standing Tier *</label>
                                            <FilterDropdown
                                                className="w-full"
                                                value={ruleFormData.standing_tier}
                                                onChange={(val) => val && setRuleFormData({ ...ruleFormData, standing_tier: val })}
                                                options={tierValues.map(t => ({ name: TIER_LABELS[t] || t, value: t }))}
                                                up
                                            />
                                        </div>
                                    </>
                                );
                            })()}

                            <div className="flex items-center gap-3 pt-2">
                                <input
                                    type="checkbox"
                                    id="rule_is_active_toggle"
                                    checked={ruleFormData.is_active}
                                    onChange={(e) => setRuleFormData({ ...ruleFormData, is_active: e.target.checked })}
                                    className="w-4 h-4 text-[#18216D] rounded border-slate-300 focus:ring-[#18216D]"
                                />
                                <label htmlFor="rule_is_active_toggle" className="text-xs font-bold text-slate-700 cursor-pointer">
                                    Active for automated cron monitoring
                                </label>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={handleCloseRuleModal}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createRuleMutation.isPending || updateRuleMutation.isPending}
                                    className="px-6 py-2.5 bg-[#18216D] hover:bg-[#121952] text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                                >
                                    {editingRule ? 'Save Changes' : 'Create Rule'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DisciplineCategoryManager;
